import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PresentationSlide } from '../types';
import { isLinkedLibrarySlide } from '../librarySlides';
import { isProductLinkedSlide } from '../pageKind';
import { COPY, slideCountText } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';
import { LibraryFollowCorner, Pill, ProductKindMark, SlideThumbnail } from '../ui';
import { focusVisibleSx } from '../ui/focus';
import { moveSlideByOffset } from './slideOrder';

interface GridViewProps {
  slides: PresentationSlide[];
  selectedIds: string[];
  pageFormat?: string | null;
  readOnly?: boolean;
  showPageKindMarks?: boolean;
  onSelect: (slideId: string, additive: boolean) => void;
  onOpen: (slideId: string) => void;
  onReorder: (sourceId: string, targetId: string) => void;
  onSectionTitle: (slideId: string, title: string) => void;
  onExit?: () => void;
}

const GridCard: React.FC<{
  slide: PresentationSlide;
  index: number;
  selected: boolean;
  readOnly: boolean;
  onSelect: (slideId: string, additive: boolean) => void;
  onOpen: (slideId: string) => void;
  onSectionTitle: (slideId: string, title: string) => void;
  onMove: (slideId: string, direction: -1 | 1) => void;
  pageFormat?: string | null;
  showPageKindMarks?: boolean;
}> = ({ slide, index, selected, readOnly, onSelect, onOpen, onSectionTitle, onMove, pageFormat, showPageKindMarks = true }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
    id: slide.slideId,
    disabled: readOnly,
  });
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(slide.sectionTitle || '');

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
      sx={{
        opacity: isDragging ? 0.55 : 1,
        position: 'relative',
        outline: isOver && !isDragging ? `2px solid ${PRES.interactive}` : 'none',
        outlineOffset: 4,
        '&:hover .grid-card-actions, &:focus-within .grid-card-actions': { opacity: 1 },
      }}
    >
      <Box
        onClick={(event) => onSelect(slide.slideId, event.shiftKey || event.metaKey || event.ctrlKey)}
        onDoubleClick={() => onOpen(slide.slideId)}
        onContextMenu={(event) => {
          event.preventDefault();
          onSelect(slide.slideId, false);
          setMenu({ x: event.clientX, y: event.clientY });
        }}
        sx={{ position: 'relative' }}
      >
        <SlideThumbnail slide={slide} selected={selected} hidden={slide.hidden} pageFormat={pageFormat} />
        {showPageKindMarks && isLinkedLibrarySlide(slide) && <LibraryFollowCorner />}
        {showPageKindMarks && isProductLinkedSlide(slide) && (
          <Box sx={{ position: 'absolute', left: 8, bottom: 8, zIndex: 2 }}>
            <ProductKindMark />
          </Box>
        )}
      </Box>
      {selected && (
        <Box sx={{
          position: 'absolute',
          top: 8,
          left: showPageKindMarks && isLinkedLibrarySlide(slide) ? 'auto' : 8,
          right: showPageKindMarks && isLinkedLibrarySlide(slide) ? 8 : 'auto',
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: PRES.interactive,
          color: PRES.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
        }}>
          <CheckIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
      {(slide.hidden || slide.locked) && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 2 }}>
          {slide.hidden && <Pill tone="warn" icon={<VisibilityOffOutlinedIcon sx={{ fontSize: 12 }} />} title={COPY.slideHidden}>{COPY.slideHidden}</Pill>}
          {slide.locked && <Pill tone="neutral" icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />} title={COPY.slideLocked}>{COPY.slideLocked}</Pill>}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, minHeight: 28 }}>
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.meta, color: PRES.textSecondary, flex: 1 }}>
          {COPY.slideLabel} {index + 1}
          {slide.sectionTitle ? ` · ${slide.sectionTitle}` : ''}
        </Typography>
        {!slide.sectionTitle && !editing && (
          <Box
            component="button"
            type="button"
            className="grid-card-actions"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              setEditing(true);
            }}
            sx={{
              opacity: selected ? 1 : 0,
              border: 0,
              bgcolor: 'transparent',
              color: PRES.interactive,
              fontFamily: TYPE.ui,
              ...TYPE.caption,
              cursor: 'pointer',
              p: 0,
              ...focusVisibleSx,
            }}
          >
            {COPY.addSection}
          </Box>
        )}
        <IconButton
          className="grid-card-actions"
          size="small"
          aria-label={COPY.slideActions}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setMenu({ x: event.clientX, y: event.clientY });
          }}
          sx={{ opacity: selected ? 1 : 0, color: PRES.textPrimary, ...focusVisibleSx }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
      {editing && (
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={title}
          placeholder={COPY.section}
          onPointerDown={(event) => event.stopPropagation()}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => {
            setEditing(false);
            onSectionTitle(slide.slideId, title.trim());
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
            if (event.key === 'Escape') setEditing(false);
          }}
          sx={{ mt: 0.5 }}
        />
      )}
      <Menu
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.y, left: menu.x } : undefined}
      >
        <MenuItem onClick={() => { setEditing(true); setMenu(null); }}>{slide.sectionTitle ? COPY.section : COPY.addSection}</MenuItem>
        <MenuItem disabled={readOnly || index === 0} onClick={() => { onMove(slide.slideId, -1); setMenu(null); }}>{COPY.moveSlideUp}</MenuItem>
        <MenuItem disabled={readOnly} onClick={() => { onMove(slide.slideId, 1); setMenu(null); }}>{COPY.moveSlideDown}</MenuItem>
      </Menu>
    </Box>
  );
};

const GridView: React.FC<GridViewProps> = ({
  slides,
  selectedIds,
  pageFormat,
  readOnly = false,
  onSelect,
  onOpen,
  onReorder,
  onSectionTitle,
  onExit,
  showPageKindMarks = true,
}) => {
  const ids = useMemo(() => slides.map((slide) => slide.slideId), [slides]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const selectedId = selectedIds[0] || slides[0]?.slideId || null;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      const current = selectedId;
      if (!current) return;
      const index = ids.indexOf(current);
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = ids[Math.min(ids.length - 1, index + 1)];
        if (next) onSelect(next, event.shiftKey);
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = ids[Math.max(0, index - 1)];
        if (next) onSelect(next, event.shiftKey);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        if (selectedIds.length > 1) onSelect(current, false);
        else onExit?.();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        onOpen(current);
      }
      if ((event.altKey || event.metaKey) && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();
        const offset = event.key === 'ArrowUp' ? -1 : 1;
        const next = moveSlideByOffset(ids, current, offset);
        const to = next.indexOf(current);
        if (to !== index && ids[to]) onReorder(current, ids[to]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ids, onExit, onOpen, onReorder, onSelect, selectedId, selectedIds.length]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: PRES.canvas }}>
      <Box sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        borderBottom: `1px solid ${PRES.borderSubtle}`,
        bgcolor: PRES.surface,
        flexShrink: 0,
      }}>
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
          {COPY.overview} · {slideCountText(slides.length)}
        </Typography>
        {selectedIds.length > 0 && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.meta, color: PRES.textSecondary }}>
            {selectedIds.length} {COPY.selectedCount}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event) => {
            const targetId = event.over?.id;
            if (!targetId || event.active.id === targetId) return;
            onReorder(String(event.active.id), String(targetId));
          }}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
              {slides.map((slide, index) => (
                <GridCard
                  key={slide.slideId}
                  slide={slide}
                  index={index}
                  selected={selectedIds.includes(slide.slideId)}
                  readOnly={readOnly}
                  onSelect={onSelect}
                  onOpen={onOpen}
                  onSectionTitle={onSectionTitle}
                  pageFormat={pageFormat}
                  showPageKindMarks={showPageKindMarks}
                  onMove={(slideId, direction) => {
                    const target = ids[ids.indexOf(slideId) + direction];
                    if (target) onReorder(slideId, target);
                  }}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
};

export default GridView;
